import { cn } from '@/lib/utils';
import { MetricTooltip } from './MetricTooltip';
import type { AmDashboardSummary, IssueFilter } from '../types';

interface UrgentCard {
  key: IssueFilter;
  label: string;
  tooltipText: string;
  color: string;
  activeColor: string;
  borderColor: string;
}

const CARDS: UrgentCard[] = [
  {
    key: 'inactive_transmitters',
    label: 'Inactive Transmitters',
    tooltipText:
      'Counts synced PostgreSQL transmitters linked to accounts in scope where activated is true but the last packet is older than 2 hours or missing, based on the latest account health snapshot.',
    color: 'text-rose-500',
    activeColor: 'ring-rose-500',
    borderColor: 'border-l-rose-500',
  },
  {
    key: 'inactive_sensors',
    label: 'Inactive Sensors',
    tooltipText:
      'Counts live synced PostgreSQL sensors linked to accounts in scope where status = 5. This matches the n8n KPI workflow sensor buckets and excludes deleted, archived, and excluded internal/test organisations.',
    color: 'text-red-500',
    activeColor: 'ring-red-500',
    borderColor: 'border-l-red-500',
  },
  {
    key: 'sensors_in_alarm',
    label: 'Sensors in Alarm',
    tooltipText:
      'Counts synced PostgreSQL sensors linked to accounts in scope where status is 2, 3, or 4, based on the latest account health snapshot.',
    color: 'text-orange-500',
    activeColor: 'ring-orange-500',
    borderColor: 'border-l-orange-500',
  },
  {
    key: 'low_battery',
    label: 'Low Battery',
    tooltipText:
      'Counts synced PostgreSQL transmitters linked to accounts in scope where activeFlag is true, batteryPercentage is below 20, and external power is not present, based on the latest account health snapshot.',
    color: 'text-yellow-500',
    activeColor: 'ring-yellow-500',
    borderColor: 'border-l-yellow-500',
  },
  {
    key: 'overdue_calibrations',
    label: 'Overdue Calibrations',
    tooltipText:
      'Counts calibration certificates linked to accounts in scope where dueDate is before today and the certificate is not locked, based on the latest account health snapshot.',
    color: 'text-purple-500',
    activeColor: 'ring-purple-500',
    borderColor: 'border-l-purple-500',
  },
];

const EMPTY_BUCKET = { count: 0, accountsAffected: 0 };

// Guards the dashboard against summary shapes that drop a bucket —
// typically when rest_server and frontend are on different code versions.
// The page renders a zeroed card instead of throwing.
function getSummaryForCard(
  summary: AmDashboardSummary,
  key: IssueFilter,
): { count: number; accountsAffected: number } {
  const map: Record<IssueFilter, { count: number; accountsAffected: number } | undefined> = {
    inactive_transmitters: summary.inactiveTransmitters,
    inactive_sensors: summary.inactiveSensors,
    sensors_in_alarm: summary.sensorsInAlarm,
    low_battery: summary.lowBatteryDevices,
    overdue_calibrations: summary.overdueCalibrations,
    expiring_contracts: summary.expiringContracts,
  };
  return map[key] ?? EMPTY_BUCKET;
}

interface UrgentStripProps {
  summary: AmDashboardSummary;
  activeFilter: IssueFilter | null;
  onFilterChange: (filter: IssueFilter | null) => void;
}

export function UrgentStrip({ summary, activeFilter, onFilterChange }: UrgentStripProps) {
  return (
    <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
      {CARDS.map((card) => {
        const data = getSummaryForCard(summary, card.key);
        const isActive = activeFilter === card.key;
        const isMuted = data.count === 0;
        const nextFilter = isActive ? null : card.key;

        return (
          <div
            key={card.key}
            role="button"
            tabIndex={0}
            onClick={() => onFilterChange(nextFilter)}
            onKeyDown={(event) => {
              if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault();
                onFilterChange(nextFilter);
              }
            }}
            className={cn(
              'rounded-lg border-l-4 bg-white p-3.5 text-left shadow-sm transition-all hover:shadow-md',
              card.borderColor,
              isActive && `ring-2 ${card.activeColor}`,
              isMuted && 'opacity-50',
            )}
          >
            <div className="flex items-center gap-1 text-xs font-medium uppercase tracking-wide text-muted-foreground">
              <span>{card.label}</span>
              <MetricTooltip
                label={`Explain ${card.label} calculation`}
                text={card.tooltipText}
              />
            </div>
            <div className={cn('my-1 text-2xl font-bold', isMuted ? 'text-muted-foreground' : card.color)}>
              {data.count}
            </div>
            <div className="text-xs text-muted-foreground">
              {card.key === 'expiring_contracts'
                ? 'within 90 days'
                : `across ${data.accountsAffected} account${data.accountsAffected !== 1 ? 's' : ''}`}
            </div>
          </div>
        );
      })}
    </div>
  );
}
